import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import type { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import { useTenant } from '../providers/TenantProvider';

type Swatch = {
  label: string;
  value: string;
};

interface SectionProps {
  title: string;
  children: ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <Box component="section" sx={{ py: 4, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

interface ColorSwatchProps {
  swatch: Swatch;
}

function ColorSwatch({ swatch }: ColorSwatchProps) {
  const theme = useTheme();
  const contrastColor = theme.palette.getContrastText(swatch.value);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          height: 92,
          bgcolor: swatch.value,
          color: contrastColor,
          display: 'flex',
          alignItems: 'flex-end',
          p: 1.5,
        }}
      >
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
          {swatch.value}
        </Typography>
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          {swatch.label}
        </Typography>
      </Box>
    </Box>
  );
}

interface LogoPreviewProps {
  label: string;
  src: string;
  size: number;
  fallback?: string;
}

function LogoPreview({ label, src, size, fallback }: LogoPreviewProps) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 2,
        height: '100%',
      }}
    >
      <Stack spacing={2} alignItems="center">
        <Box
          sx={{
            width: 132,
            height: 132,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Box
            component="img"
            src={src}
            alt={label}
            sx={{
              width: size,
              height: size,
              objectFit: 'contain',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" fontWeight={700}>
            {label}
          </Typography>
          {fallback && (
            <Typography variant="caption" color="text.secondary">
              {fallback}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export const UiKitPage = () => {
  const theme = useTheme();
  const { tenantId, tenants, role } = useTenant();
  const [selectedControl, setSelectedControl] = useState('list');

  const activeTenant = tenants.find((tenant) => tenant.tenantId === tenantId) || null;
  const tenantBranding = activeTenant?.branding || null;
  const tenantLogo = tenantBranding?.logoUrl || '/logo.png';
  const tenantPrimary = tenantBranding?.primaryColor || theme.palette.primary.main;
  const tenantSecondary = tenantBranding?.secondaryColor || theme.palette.secondary.main;

  const handleControlChange: ToggleButtonGroupProps['onChange'] = (_event, value) => {
    if (value) setSelectedControl(value);
  };

  const globalSwatches: Swatch[] = [
    { label: 'Primary', value: theme.palette.primary.main },
    { label: 'Primary dark', value: theme.palette.primary.dark },
    { label: 'Secondary', value: theme.palette.secondary.main },
    { label: 'Error', value: theme.palette.error.main },
    { label: 'Warning', value: theme.palette.warning.main },
    { label: 'Info', value: theme.palette.info.main },
    { label: 'Success', value: theme.palette.success.main },
    { label: 'Grey 50', value: theme.palette.grey[50] },
  ];

  const tenantSwatches: Swatch[] = [
    { label: 'Tenant primary', value: tenantPrimary },
    { label: 'Tenant secondary', value: tenantSecondary },
  ];

  const neutralSwatches: Swatch[] = [
    { label: 'Background', value: theme.palette.background.default },
    { label: 'Paper', value: theme.palette.background.paper },
    { label: 'Text primary', value: theme.palette.text.primary },
    { label: 'Text secondary', value: theme.palette.text.secondary },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaletteIcon />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                UI Kit
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Brand, typography, color, and component samples for the active tenant experience.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Section title="Brand">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <LogoPreview label="Product logo" src="/logo.png" size={100} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LogoPreview
                label={activeTenant?.name ? `${activeTenant.name} logo` : 'Tenant logo'}
                src={tenantLogo}
                size={100}
                fallback={tenantBranding?.logoUrl ? undefined : 'Using product fallback'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LogoPreview label="Small logo" src="/logo.png" size={32} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LogoPreview label="Nav logo" src="/logo.png" size={40} />
            </Grid>
          </Grid>
        </Section>

        <Section title="Typography">
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  p: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Primary typeface
                </Typography>
                <Typography variant="h5" gutterBottom>
                  Helvetica
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Font stack
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                  {theme.typography.fontFamily}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={1.25}>
                <Typography variant="h4">Page heading</Typography>
                <Typography variant="h5">Section heading</Typography>
                <Typography variant="h6">Panel heading</Typography>
                <Typography variant="body1">Body text for primary content and summaries.</Typography>
                <Typography variant="body2" color="text.secondary">
                  Secondary body text for supporting metadata.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Caption text for compact labels.
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  GYM-1700000000-ABC123
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Section>

        <Section title="Colors">
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Global palette
              </Typography>
              <Grid container spacing={2}>
                {globalSwatches.map((swatch) => (
                  <Grid key={`${swatch.label}-${swatch.value}`} item xs={12} sm={6} md={3}>
                    <ColorSwatch swatch={swatch} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Active tenant
              </Typography>
              <Grid container spacing={2}>
                {tenantSwatches.map((swatch) => (
                  <Grid key={`${swatch.label}-${swatch.value}`} item xs={12} sm={6} md={3}>
                    <ColorSwatch swatch={swatch} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Neutral surfaces
              </Typography>
              <Grid container spacing={2}>
                {neutralSwatches.map((swatch) => (
                  <Grid key={`${swatch.label}-${swatch.value}`} item xs={12} sm={6} md={3}>
                    <ColorSwatch swatch={swatch} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>
        </Section>

        <Section title="Buttons And Controls">
          <Stack spacing={3}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button variant="contained" startIcon={<SaveIcon />}>
                Save
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />}>
                Add Item
              </Button>
              <Button variant="text">Text Button</Button>
              <Button variant="contained" color="secondary">
                Secondary
              </Button>
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />}>
                Delete
              </Button>
              <Button variant="contained" disabled>
                Disabled
              </Button>
            </Stack>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="contained" size="small">
                Small
              </Button>
              <Button variant="contained" size="medium">
                Medium
              </Button>
              <Button variant="contained" size="large">
                Large
              </Button>
              <IconButton color="primary" aria-label="confirm">
                <CheckCircleIcon />
              </IconButton>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={selectedControl}
                onChange={handleControlChange}
                aria-label="view mode"
              >
                <ToggleButton value="list">List</ToggleButton>
                <ToggleButton value="grid">Grid</ToggleButton>
                <ToggleButton value="calendar">Calendar</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        </Section>

        <Section title="Component Examples">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <TextField label="Text field" defaultValue="Owner account" fullWidth />
                <TextField label="Focused field" defaultValue="tenant-demo-gym" focused fullWidth />
                <TextField label="Error field" defaultValue="bad-email" error helperText="Use a valid email address." fullWidth />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={role?.toUpperCase() || 'ROLE'} color="primary" />
                  <Chip label="ACTIVE" color="success" />
                  <Chip label="PENDING" color="warning" />
                  <Chip label="DISABLED" />
                </Stack>
                <Alert severity="success">Success state example.</Alert>
                <Alert severity="warning">Warning state example.</Alert>
                <Alert severity="error">Error state example.</Alert>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Surface Example
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cards use a restrained radius, clear spacing, and visible hierarchy for scannable admin workflows.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small">Open</Button>
                  <Button size="small" variant="contained">
                    Confirm
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Section>
      </Container>
    </Box>
  );
};

export default UiKitPage;
